import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { EditColumnsDropdown } from './components/edit-columns-dropdown';
import { EditLocationModal } from './components/edit-location-modal';
import { LocationsImportDrawer } from './components/locations-import/locations-import-drawer';
import { Table } from './components/table';
import { useExportLocationsCSV } from './hooks/use-export-locations-csv';
import messages from './messages';
import style from './style.module.less';
import { ImportStatus } from '../../../../../components/csv-import/data/types/import-status';
import { ImportType } from '../../../../../components/csv-import/data/types/import-type';
import ModalPortal from '../../../../../components/modal-portal';
import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableInventoryLocationCsvImport } from '../../../../../feature-flags/hooks/use-enable-inventory-location-csv-import';
import { useEnableInventoryLocationsBarcode } from '../../../../../feature-flags/hooks/use-enable-inventory-locations-barcode';
import { useModal } from '../../../../../hooks/use-modal';
import { useLocale } from '../../../../../intl/hooks';
import Global from '../../../../../store/global-selectors';
import { deleteInventoryWarehouseLocation, updateInventoryWarehouseLocation } from '../../../../../store/inventory/inventory.api';
import { updateSellerInventoryWarehousesLocations } from '../../../../../store/seller/seller.actions';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { baseColumns, baseColumnsWithBarcode } from '../../constants';
import { usePrintLocationBarcode } from '../../hooks/use-print-location-barcode';
import DeleteModal from '../../modals/delete-modal';

const MODALS = {
  addLocationModal: 'addLocationModal',
  deleteLocationModal: 'deleteLocationModal',
};

const useComparer = (propertyArray, locale) => {
  return useMemo(
    () => (a, b) => {
      return propertyArray.reduce((acc, property) => {
        if (acc !== 0) return acc;

        if (!property || a[property] === b[property]) {
          return 0;
        }

        if (!a[property]) {
          return 1;
        }

        return a[property].localeCompare(b[property], locale);
      }, 0);
    },
    [propertyArray, locale],
  );
};

/**
 * Tab Component for managing inventory locations.
 * @param {object} params
 * @param {string} params.inventoryWarehouseId - The ID of the inventory warehouse.
 * @param {string} params.inventoryWarehouseName - The name of the inventory warehouse.
 * @returns {JSX.Element} The rendered tab component.
 */
const ManageLocationsTab = ({ inventoryWarehouseId, inventoryWarehouseName }) => {
  const dispatch = useDispatch();
  const { locale } = useLocale();
  const isInventoryLocationsBarcodeEnabled = useEnableInventoryLocationsBarcode();
  const [openModal, setOpenModal] = useAsyncSafeState();
  const [columnsToDisplay, setColumnsToDisplay] = useState(isInventoryLocationsBarcodeEnabled ? baseColumnsWithBarcode : baseColumns);
  const [shouldShowInactiveLocations, setShouldShowInactiveLocations] = useState(false);
  const [selectedIds, setSelectedIdsState] = useState([]);
  const prevWarehouseIdRef = useRef(inventoryWarehouseId);
  const lastSelectedRowId = useRef(null);

  const columnKeys = useMemo(() => columnsToDisplay.map((column) => column.mappedKey), [columnsToDisplay]);
  const comparer = useComparer(columnKeys, locale);

  const { onPrint: printLocationBarcodes, isDisabled: isPrintDisabled } = usePrintLocationBarcode({
    locationIds: selectedIds,
  });

  const setSelectedIds = useCallback((ids) => {
    setSelectedIdsState(typeof ids === 'function' ? ids : () => ids);
  }, []);

  useEffect(() => {
    if (prevWarehouseIdRef.current !== inventoryWarehouseId) {
      setSelectedIds([]);
      lastSelectedRowId.current = null;
      prevWarehouseIdRef.current = inventoryWarehouseId;
    }
  }, [inventoryWarehouseId, setSelectedIds]);

  const inventoryWarehouseLocations =
    useSelector((state) =>
      Global.selectSellerInventoryWarehouseLocationsByWarehouseId(state, {
        inventoryWarehouseId,
      }),
    ) ?? emptyArray;

  const filteredData = useMemo(() => {
    const filtered = inventoryWarehouseLocations.filter((location) =>
      shouldShowInactiveLocations ? location : location.isActive === true,
    );
    const unspecifiedLocations = filtered.filter((location) => location.isUnspecified);
    const sorted = filtered.filter((loc) => !loc.isUnspecified).sort(comparer);
    return [...unspecifiedLocations, ...sorted];
  }, [inventoryWarehouseLocations, shouldShowInactiveLocations, comparer]);

  const selectableIdsSet = useMemo(
    () => new Set(filteredData.filter((loc) => !loc.isUnspecified).map((loc) => loc.inventoryLocationId)),
    [filteredData],
  );

  const handleSelectAll = useCallback(() => {
    const selectableLocations = filteredData.filter((location) => !location.isUnspecified);
    const selectedSelectableCount = selectedIds.filter((id) => selectableIdsSet.has(id)).length;
    const allSelectableSelected = selectableLocations.length > 0 && selectedSelectableCount === selectableLocations.length;
    setSelectedIds(allSelectableSelected ? [] : selectableLocations.map((location) => location.inventoryLocationId));
    lastSelectedRowId.current = null;
  }, [filteredData, selectedIds, selectableIdsSet, setSelectedIds]);

  const handleSelectOne = useCallback(
    (id, event) => {
      if (event?.nativeEvent?.shiftKey && lastSelectedRowId.current !== null) {
        const allIds = filteredData.map((location) => location.inventoryLocationId);
        const currentIndex = allIds.indexOf(id);
        const lastIndex = allIds.indexOf(lastSelectedRowId.current);

        if (currentIndex === -1 || lastIndex === -1) {
          return;
        }

        const startIndex = Math.min(currentIndex, lastIndex);
        const endIndex = Math.max(currentIndex, lastIndex);
        const rangeIds = allIds.slice(startIndex, endIndex + 1);

        const selectableRangeIds = rangeIds.filter((rangeId) => selectableIdsSet.has(rangeId));

        setSelectedIds((prev) => {
          const isCurrentlySelected = prev.includes(id);
          if (isCurrentlySelected) {
            const idsToDeselect = new Set(selectableRangeIds);
            return prev.filter((selectedId) => !idsToDeselect.has(selectedId));
          }
          return [...new Set([...prev, ...selectableRangeIds])];
        });
      } else {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
      }

      lastSelectedRowId.current = id;
    },
    [setSelectedIds, filteredData, selectableIdsSet],
  );

  const openAddLocationModal = useCallback(() => setOpenModal(MODALS.addLocationModal), [setOpenModal]);
  const openDeleteLocationModal = useCallback(() => setOpenModal(MODALS.deleteLocationModal), [setOpenModal]);
  const closeModal = useCallback(() => setOpenModal(undefined), [setOpenModal]);

  const onCheckboxChange = () => {
    setShouldShowInactiveLocations((prev) => !prev);
  };

  const handleDeleteLocations = useCallback(() => {
    Promise.all(
      selectedIds.map((locationId) =>
        deleteInventoryWarehouseLocation(locationId).promise.then(({ data, errors }) => {
          if (errors?.length > 0) {
            throw new Error(errors[0]);
          }
          dispatch(updateSellerInventoryWarehousesLocations(data));
          return data;
        }),
      ),
    )
      .then(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <Text textType={TextType.Paragraph} value={messages.deleteLocationsSuccessTitle} />,
          content: <FormattedMessage {...messages.deleteLocationsSuccessContent} values={{ count: selectedIds.length }} />,
          dontHide: true,
        });
        setSelectedIds([]);
        closeModal();
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <Text textType={TextType.Paragraph} value={messages.deleteLocationsFailTitle} />,
          content: <Text textType={TextType.Paragraph} value={messages.deleteLocationsFailContent} />,
          dontHide: true,
        });
        closeModal();
      });
  }, [selectedIds, dispatch, setSelectedIds, closeModal]);

  const handlePrintBarcodes = useCallback(() => {
    printLocationBarcodes('location-barcode');
  }, [printLocationBarcodes]);

  const handleGenerateBarcodes = useCallback(async () => {
    try {
      const selectedLocations = inventoryWarehouseLocations.filter((location) => selectedIds.includes(location.inventoryLocationId));
      const locationsWithoutBarcode = selectedLocations.filter((location) => !location.barcode);

      if (locationsWithoutBarcode.length === 0) {
        return;
      }

      const timestamp = DateTime.now().toMillis();
      const updatedLocations = locationsWithoutBarcode.map((location) => ({
        ...location,
        barcode: `${timestamp + location.inventoryLocationId}`,
      }));

      const { data, errors } = await updateInventoryWarehouseLocation(updatedLocations).promise;
      if (errors?.length > 0) {
        throw new Error(errors[0]);
      }

      if (data && data.length > 0) {
        dispatch(updateSellerInventoryWarehousesLocations(data));
      }
      setSelectedIds([]);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <Text textType={TextType.Paragraph} value={messages.generateBarcodesSuccessTitle} />,
        content: <FormattedMessage {...messages.generateBarcodesSuccessContent} values={{ count: locationsWithoutBarcode.length }} />,
        dontHide: true,
      });
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <Text textType={TextType.Paragraph} value={messages.generateBarcodesErrorTitle} />,
        content: <Text textType={TextType.Paragraph} value={messages.generateBarcodesErrorMessage} />,
        dontHide: true,
      });
    }
  }, [inventoryWarehouseLocations, selectedIds, dispatch, setSelectedIds]);
  const selectedLocationsWithoutBarcode = useMemo(() => {
    const selectedLocations = inventoryWarehouseLocations.filter((location) => selectedIds.includes(location.inventoryLocationId));
    return selectedLocations.filter((location) => !location.barcode);
  }, [inventoryWarehouseLocations, selectedIds]);
  const { close: closeLocationsImport, isOpen: isLocationsImportOpen, open: openLocationsImport } = useModal();

  const trackWorkflow = useTrackWorkflow(Workflows.CSV_INVENTORY_LOCATION_IMPORT_FLOW);

  const latestImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.inventoryLocation));

  const onOpen = () => {
    if (latestImport.status !== ImportStatus.ACTIVE) {
      trackWorkflow(WorkflowSteps.START, { version: 2 });
    }
    openLocationsImport();
  };

  const enableInventoryLocationImport = useEnableInventoryLocationCsvImport();
  const exportLocationsCSV = useExportLocationsCSV(inventoryWarehouseId);

  return (
    <FlexColumn className={style['manage-locations-container']} gap={Gap.Space24}>
      <FlexColumn gap={Gap.Space20}>
        <Text textType={TextType.Paragraph} value={messages.inventoryLocationsTabDescription} />
        <FlexRow className={style['table-actions']} gap={Gap.Space16}>
          <Checkbox
            checked={shouldShowInactiveLocations}
            label={<FormattedMessage {...messages.manageLocationsModalGridShowInactiveCheckbox} />}
            onChange={onCheckboxChange}
          />
          <FlexRow className={style.buttons}>
            {isInventoryLocationsBarcodeEnabled ? (
              <>
                <Button
                  disabled={!selectedLocationsWithoutBarcode.length}
                  onClick={handleGenerateBarcodes}
                  theme={THEME_CONSTANTS.secondary}
                >
                  <FormattedMessage {...messages.generateBarcode} />
                </Button>
                <Button disabled={isPrintDisabled} onClick={handlePrintBarcodes} theme={THEME_CONSTANTS.secondary}>
                  <FormattedMessage {...messages.printBarcode} />
                </Button>
              </>
            ) : null}
            <Button disabled={!selectedIds.length} onClick={openDeleteLocationModal} theme={THEME_CONSTANTS.secondary}>
              <FormattedMessage {...messages.deleteButton} />
            </Button>
            {enableInventoryLocationImport ? (
              <>
                <Button onClick={onOpen} theme={THEME_CONSTANTS.tertiary}>
                  <FormattedMessage {...messages.importCsv} />
                </Button>
                <Button onClick={exportLocationsCSV} theme={THEME_CONSTANTS.tertiary}>
                  <FormattedMessage {...messages.exportCsv} />
                </Button>
              </>
            ) : null}
            {isLocationsImportOpen ? (
              <LocationsImportDrawer
                inventoryWarehouseId={inventoryWarehouseId}
                onClose={closeLocationsImport}
                trackProcess={trackWorkflow}
              />
            ) : null}
            <EditColumnsDropdown columnsToDisplay={columnsToDisplay} setColumnsToDisplay={setColumnsToDisplay} />
            <Button className={style['add-location-button']} onClick={openAddLocationModal} theme={THEME_CONSTANTS.primary}>
              <FontAwesomeIcon className={style['button-icon']} icon={faPlus} />
              <FormattedMessage {...messages.addLocationButtonLabel} className={style['button-text']} />
            </Button>
          </FlexRow>
        </FlexRow>
      </FlexColumn>
      <Table
        columnsToDisplay={columnsToDisplay}
        handleSelectAll={handleSelectAll}
        handleSelectOne={handleSelectOne}
        inventoryWarehouseLocations={filteredData}
        inventoryWarehouseName={inventoryWarehouseName}
        selectedIds={selectedIds}
      />
      {openModal === MODALS.addLocationModal && (
        <ModalPortal onClose={closeModal}>
          <EditLocationModal inventoryWarehouseId={inventoryWarehouseId} onClose={closeModal} />
        </ModalPortal>
      )}
      {openModal === MODALS.deleteLocationModal && (
        <ModalPortal onClose={closeModal}>
          <DeleteModal
            inventoryWarehouseLocationId={inventoryWarehouseId}
            inventoryWarehouseName={inventoryWarehouseName}
            onDelete={handleDeleteLocations}
          />
        </ModalPortal>
      )}
    </FlexColumn>
  );
};

export { ManageLocationsTab };
